import { LocalStorageKeys } from "@/constants/local-storage-keys";
import { MixpanelEvents } from "@/constants/mixpanel-events";
import { aiChatModalOpenAtom, chatMessagesAtom, shouldResumeChat } from "@/stores/chat.store";
import { trackMixpanelEvent } from "@/utils/mixpanel";
import { useAuth } from "@clerk/clerk-react";
import { useAtomValue, useSetAtom, useStore } from "jotai";
import { useEffect, useRef } from "react";

const FLAG_VALUE = "1";

/**
 * Re-opens the AI chat on the *same* session after an unexpected app restart
 * (WebView memory-pressure kill, service-worker / chunk reload). The chat sheet
 * is driven by the in-memory `aiChatModalOpenAtom`, so any reload closes it —
 * and nothing else reopens an in-flight conversation. Every entry-point hook
 * (`useOpenMealCoach`, `useOpenCoachChat`) mints a fresh session id, which is
 * how a mid-conversation restart silently split a chat into two server sessions
 * and lost the agent's context.
 *
 * Mechanism mirrors `ChatPhotoRestorationListener`: a persisted `chat-open` flag
 * tracks the live modal state. A deliberate dismiss clears it (mirror effect
 * below); a crash leaves it set because the dismiss path never runs. On the next
 * mount we read the boot value and, if a resumable conversation exists, reopen
 * the sheet without touching the persisted session id or transcript.
 *
 * Intentionally NOT web-guarded: a PWA reload (e.g. an SW update) benefits too.
 * Call once, from the single `AiChatModal` host.
 */
export const useChatResumeOnRestart = (): void => {
  const store = useStore();
  const isOpen = useAtomValue(aiChatModalOpenAtom);
  const setOpen = useSetAtom(aiChatModalOpenAtom);
  const { isLoaded, isSignedIn } = useAuth();

  // Snapshot the flag at first render, before the mirror effect below can
  // rewrite it — the resume decision keys off this captured value, not the
  // live (possibly already-cleared) localStorage entry.
  const wasOpenOnBoot = useRef(localStorage.getItem(LocalStorageKeys.CHAT_OPEN_KEY) === FLAG_VALUE);
  const didResume = useRef(false);

  // One-shot boot resume. Waits for Clerk to load so the signed-in gate and the
  // user-scoped transcript are trustworthy. Reading `chatMessagesAtom` applies
  // the 5-min TTL purge, so an expired conversation resolves to [] and is not
  // resumed (and its session id is dropped by the purge).
  useEffect(() => {
    if (!isLoaded || didResume.current) return;
    didResume.current = true;

    if (!wasOpenOnBoot.current) return;

    const messages = store.get(chatMessagesAtom);
    if (shouldResumeChat({ wasOpenOnBoot: true, isSignedIn: !!isSignedIn, messages })) {
      setOpen(true);
      trackMixpanelEvent(MixpanelEvents.AI_CHAT_RESUMED_AFTER_RESTART, {
        message_count: messages.length,
      });
    } else {
      // Stale flag (expired / greeting-only / signed-out) — drop it so a later
      // unrelated boot doesn't try to resume.
      localStorage.removeItem(LocalStorageKeys.CHAT_OPEN_KEY);
    }
  }, [isLoaded, isSignedIn, store, setOpen]);

  // Mirror the live modal state to localStorage. A deliberate dismiss flips the
  // atom to false and clears the flag; a restart never runs this with `false`,
  // so the flag survives and the resume effect fires on the next boot.
  useEffect(() => {
    if (isOpen) {
      localStorage.setItem(LocalStorageKeys.CHAT_OPEN_KEY, FLAG_VALUE);
    } else {
      localStorage.removeItem(LocalStorageKeys.CHAT_OPEN_KEY);
    }
  }, [isOpen]);
};
